/**
 * index.js - Point d'entrée de l'application React
 * 
 * Ce fichier est le PREMIER fichier exécuté quand l'application démarre.
 * C'est le point de départ de toute l'interface d'administration REMA.
 * 
 * Concepts importants expliqués:
 * =================================
 * 
 * 1. Le DOM (Document Object Model)
 *    - Le DOM est la représentation de la page HTML en mémoire
 *    - Le navigateur affiche le DOM à l'écran
 *    - Quand on modifie le DOM, la page change
 * 
 * 2. React et le Virtual DOM
 *    - React crée une copie du DOM appelée "Virtual DOM"
 *    - Quand l'état change, React modifie le Virtual DOM
 *    - React compare le Virtual DOM avec le vrai DOM
 *    - React met à jour uniquement ce qui a changé (très performant)
 * 
 * 3. Le "Root" React
 *    - Le root est le point où React attache l'application au DOM
 *    - Dans le fichier HTML, il y a une balise <div id="root"></div>
 *    - React va remplir cette div avec toute l'application
 * 
 * 4. BrowserRouter
 *    - C'est un composant qui permet la navigation dans l'application
 *    - Il utilise l'URL du navigateur pour savoir quelle page afficher
 *    - Il permet d'utiliser les boutons "précédent" et "suivant" du navigateur
 * 
 * Ce fichier fait 4 choses principales:
 * 1. Importer les bibliothèques nécessaires
 * 2. Importer les styles globaux (couleurs, polices)
 * 3. Créer le point d'attachement au DOM (le root)
 * 4. "Monter" (afficher) l'application dans le root
 */

import React from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import App from "./App";
import "./shared/theme/colors.css";

// ============================================================================
// CRÉATION DU ROOT REACT
// ============================================================================

/**
 * document.getElementById("root")
 * Cette fonction cherche dans la page HTML une balise avec l'attribut id="root"
 * Dans le fichier index.html, on trouve: <div id="root"></div>
 * C'est dans cette div que React va afficher toute l'application
 */
const elementRoot = document.getElementById("root");

/**
 * createRoot(elementRoot)
 * Cette fonction crée un "root" React attaché à l'élément HTML
 * Le root est le point de départ de l'application React
 * À partir de ce root, React va gérer tout ce qui est affiché dans cette div
 * 
 * Note: createRoot est la méthode moderne (React 18+)
 * L'ancienne méthode était ReactDOM.render() qui est maintenant dépréciée
 */
const rootReact = createRoot(elementRoot);

// ============================================================================
// MONTAGE DE L'APPLICATION
// ============================================================================

/**
 * rootReact.render(...)
 * Cette fonction "monte" l'application dans le root
 * Elle prend en paramètre un composant React (ici <BrowserRouter><App /></BrowserRouter>)
 * React va afficher ce composant et tous ses composants enfants dans la page
 * 
 * Structure:
 * <BrowserRouter>
 *   <App />
 * </BrowserRouter>
 * 
 * BrowserRouter: Active la navigation dans l'application
 * App: Le composant principal qui contient toutes les pages
 */
rootReact.render(
  <BrowserRouter>
    <App />
  </BrowserRouter>
);
